import React, {
  createContext,
  useContext,
  useReducer,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import { Simulation } from 'types/Simulation';
import { StatutPrecarite } from 'types/ResultatVolumeCEE';

type Action =
  | { type: 'ADD_SIMULATION'; payload: LocalSimulation }
  | { type: 'REMOVE_SIMULATION'; payload: string }
  | { type: 'REMOVE_ALL_SIMULATIONS' }
  | { type: 'SET_PENDING_FOST'; payload: string | null };

export type LocalSimulation = Simulation & {
  id: string;
  documentsJustificatifsOperation?: string[];
  visiteTechnique?: boolean;
  statutPrecarite?: StatutPrecarite;
};

interface SimulationsState {
  simulations: LocalSimulation[];
  pendingFostId: string | null;
}

const initialState: SimulationsState = {
  simulations: [],
  pendingFostId: null,
};

const simulationsReducer = (
  state: SimulationsState,
  action: Action,
): SimulationsState => {
  switch (action.type) {
    // Afin d'éviter les doublons, on ajoute une simulation si elle n'existe pas déjà
    case 'ADD_SIMULATION': {
      const existingSimulation = state.simulations.find(
        (sim) => sim.id === action.payload.id,
      );
      if (existingSimulation) {
        return state;
      }
      return { ...state, simulations: [...state.simulations, action.payload] };
    }
    case 'REMOVE_SIMULATION':
      return {
        ...state,
        simulations: state.simulations.filter(
          (simulation) => simulation.id !== action.payload,
        ),
      };
    case 'REMOVE_ALL_SIMULATIONS':
      return { ...state, simulations: [], pendingFostId: null };
    case 'SET_PENDING_FOST':
      return { ...state, pendingFostId: action.payload };
    default:
      return state;
  }
};

interface SimulationsContextProps {
  state: LocalSimulation[];
  pendingFostId: string | null;
  dispatch: React.Dispatch<Action>;
  addSimulation: (
    simulation: Simulation,
    documentsJustificatifsOperation?: string[],
    visiteTechnique?: boolean,
    statutPrecarite?: StatutPrecarite,
  ) => void;
  removeSimulation: (simulationId: string) => void;
  removeAllSimulations: (e?: React.MouseEvent) => void;
  setPendingFost: (fostId: string | null) => void;
}

export const SimulationsContext = createContext<
  SimulationsContextProps | undefined
>(undefined);

export const SimulationsProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(simulationsReducer, initialState);

  const removeAllSimulations = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    dispatch({ type: 'REMOVE_ALL_SIMULATIONS' });
  }, []);

  const removeSimulation = useCallback((simulationId: string) => {
    dispatch({ type: 'REMOVE_SIMULATION', payload: simulationId });
  }, []);

  const addSimulation = useCallback(
    (
      simulation: LocalSimulation,
      documentsJustificatifsOperation?: string[],
      visiteTechnique?: boolean,
      statutPrecarite?: StatutPrecarite,
    ) => {
      dispatch({
        type: 'ADD_SIMULATION',
        payload: {
          ...simulation,
          documentsJustificatifsOperation,
          visiteTechnique,
          statutPrecarite,
        },
      });
    },
    [],
  );

  const setPendingFost = useCallback((fostId: string | null) => {
    dispatch({ type: 'SET_PENDING_FOST', payload: fostId });
  }, []);

  const contextValue = useMemo(
    () => ({
      state: state.simulations,
      pendingFostId: state.pendingFostId,
      dispatch,
      addSimulation,
      removeSimulation,
      removeAllSimulations,
      setPendingFost,
    }),
    [state, addSimulation, removeSimulation, removeAllSimulations, setPendingFost],
  );

  return (
    <SimulationsContext.Provider value={contextValue}>
      {children}
    </SimulationsContext.Provider>
  );
};

/**
 * Liste les simulations effectuées pendant la session de l'utilisateur
 * @returns Le contexte des simulations
 */
export const useCurrentSimulations = () => {
  return useContext(SimulationsContext)!;
};
